<template>
    <div class="careers-page">
        <div class="ambient-bg">
            <div class="orb-a"></div>
            <div class="orb-b"></div>
            <div class="ambient-grid"></div>
        </div>

        <!-- HERO -->
        <section class="careers-hero">
            <v-container>
                <div class="hero-badge mb-5">
                    <span class="pulse-dot"></span>
                    <span>Careers · Opening soon</span>
                </div>
                <h1 class="display-xl mb-5">
                    We're not hiring yet.<br />
                    <span class="sophae-grad">But we will be, and soon.</span>
                </h1>
                <p class="hero-lede">
                    Sophae is being built by a small, deliberate team. When we open roles, they'll be for the kind of
                    people who want to do the most important work of their career. If that sounds like you, leave your
                    details. You'll be the first to hear.
                </p>
            </v-container>
        </section>

        <!-- WHO WE'RE BUILDING FOR -->
        <section class="content-section who-section">
            <v-container class="content-container">
                <div class="section-head">
                    <div class="eyebrow">The kind of team we're building</div>
                    <h2 class="display-lg mt-3 mb-5">
                        Substance over surface.<br />
                        <span class="sophae-grad-cool">Compounding over hype.</span>
                    </h2>
                    <p class="section-lede">
                        We're building Sophae for organizations that intend to outlast cycles, leaders, and trends. The
                        team we're building reflects that: small, deeply capable, and aligned on doing work that
                        matters years from now.
                    </p>
                </div>

                <div class="who-grid mt-12">
                    <div v-for="(item, i) in qualities" :key="i" class="who-card">
                        <v-icon size="22" color="amber lighten-1" class="mb-3">{{ item.icon }}</v-icon>
                        <div class="who-name">{{ item.name }}</div>
                        <p class="who-body">{{ item.body }}</p>
                    </div>
                </div>
            </v-container>
        </section>

        <!-- INTEREST FORM -->
        <section class="content-section form-section" id="interest">
            <v-container class="content-container">
                <div class="form-card">
                    <v-row>
                        <v-col cols="12" md="5">
                            <div class="eyebrow">Be first in line</div>
                            <h2 class="display-lg mt-3 mb-4">
                                Tell us<br />
                                <span class="sophae-grad">who you are.</span>
                            </h2>
                            <p class="form-lede">
                                When roles open, the people on this list hear first, before any public posting. No
                                marketing, no spam, no follow-up unless something opens that fits.
                            </p>
                            <div class="form-meta">
                                <div class="meta-item">
                                    <v-icon small color="amber lighten-1">mdi-shield-check</v-icon>
                                    <span>Your information is treated as confidential</span>
                                </div>
                                <div class="meta-item">
                                    <v-icon small color="amber lighten-1">mdi-email-outline</v-icon>
                                    <span>You can also reach us at <a
                                            href="mailto:careers@sophae.ai">careers@sophae.ai</a></span>
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12" md="7">
                            <div class="form-inner">
                                <v-form ref="careerForm" v-model="formValid" lazy-validation>
                                    <v-row dense>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="form.firstName" label="First name" outlined dense
                                                dark color="amber lighten-2" :rules="[v => !!v || 'Required']" />
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="form.lastName" label="Last name" outlined dense dark
                                                color="amber lighten-2" :rules="[v => !!v || 'Required']" />
                                        </v-col>
                                        <v-col cols="12">
                                            <v-text-field v-model="form.email" label="Email" outlined dense dark
                                                color="amber lighten-2" :rules="emailRules" />
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-select v-model="form.focus" :items="focusOptions"
                                                label="Where you'd want to contribute" outlined dense dark
                                                color="amber lighten-2" />
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="form.link" label="LinkedIn / portfolio (optional)"
                                                outlined dense dark color="amber lighten-2" />
                                        </v-col>
                                        <v-col cols="12">
                                            <v-textarea v-model="form.note"
                                                label="Anything else you'd want us to know (optional)" outlined dense
                                                dark color="amber lighten-2" rows="3" />
                                        </v-col>
                                    </v-row>

                                    <div class="d-flex justify-end mt-2">
                                        <v-btn class="sophae-cta-btn" :disabled="!formValid" @click="submit">
                                            Submit interest
                                            <v-icon right size="16">mdi-arrow-right</v-icon>
                                        </v-btn>
                                    </div>
                                </v-form>
                            </div>
                        </v-col>
                    </v-row>
                </div>
            </v-container>
        </section>

        <v-snackbar v-model="snack.open" :timeout="3200" top right color="amber darken-3">
            <span class="black--text font-weight-medium">
                {{ snack.message }}
            </span>
        </v-snackbar>
    </div>
</template>

<script>
export default {
    name: 'CareersView',
    data() {
        return {
            formValid: true,
            snack: { open: false, message: '' },
            form: {
                firstName: '',
                lastName: '',
                email: '',
                focus: '',
                link: '',
                note: '',
            },
            focusOptions: [
                'Engineering',
                'Design',
                'Product',
                'Research',
                'Go-to-market',
                'Operations',
                'Other',
            ],
            emailRules: [
                v => !!v || 'Required',
                v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '') || 'Enter a valid email',
            ],
            qualities: [
                {
                    icon: 'mdi-target',
                    name: 'High agency',
                    body: 'You take ownership of outcomes, not just tasks. You move on the things that matter without waiting to be told.',
                },
                {
                    icon: 'mdi-microscope',
                    name: 'Substance over surface',
                    body: 'You care about the thing actually working, not how it presents. You can tell the difference and the gap bothers you.',
                },
                {
                    icon: 'mdi-message-alert-outline',
                    name: 'Candid by default',
                    body: 'You say what you actually think. You hold positions, you change them when the evidence does, and you welcome being challenged.',
                },
                {
                    icon: 'mdi-graph-outline',
                    name: 'Long-game thinking',
                    body: 'You optimize for what compounds, not what looks good this quarter. You\'re building things that will outlast you.',
                },
                {
                    icon: 'mdi-handshake-outline',
                    name: 'Earned trust',
                    body: 'You don\'t need to be the loudest in the room. You build credibility through what you ship and how you handle the hard moments.',
                },
                {
                    icon: 'mdi-rocket-launch-outline',
                    name: 'Builder energy',
                    body: 'You want to make things, ship them, and watch them work in the world. Pre-product-market-fit pace doesn\'t scare you.',
                },
            ],
        }
    },
    methods: {
        submit() {
            if (this.$refs.careerForm && !this.$refs.careerForm.validate()) return

            const subject = encodeURIComponent(
                `Sophae careers: ${this.form.firstName} ${this.form.lastName}`
            )
            const body = encodeURIComponent(
                `Name: ${this.form.firstName} ${this.form.lastName}\n` +
                `Email: ${this.form.email}\n` +
                `Focus: ${this.form.focus}\n` +
                `Link: ${this.form.link}\n\n` +
                `Note:\n${this.form.note}\n`
            )
            window.location.href = `mailto:careers@sophae.ai?subject=${subject}&body=${body}`

            this.snack.message = "Thanks. You're on the list."
            this.snack.open = true
        },
    },
}
</script>
